import { useController, useFormContext } from 'react-hook-form';
import { graphql, type ResultOf } from '@/gql/graphql';
import Box from '../UI/Box';
import Select from '../UI/Select';
import UIText from '../UI/Text';
import TextInput from '../Unistyles/TextInput';
import { attributeTypeLabel } from './BadgeEdit/BadgeEdit.types';
export const BadgeAssigneeEditAttributeFragment = graphql(/* GraphQL */ `
  fragment BadgeAssigneeEditAttribute on badge_attribute {
    id
    title
    options
    is_required
    attribute_type
    default_value
  }
`);
function BadgeAssigneeEditAttribute({
  name,
  attribute,
  inlineMetadata = false,
  isBottomSheet = false,
}: {
  name: string;
  attribute: ResultOf<typeof BadgeAssigneeEditAttributeFragment>;
  inlineMetadata?: boolean;
  isBottomSheet?: boolean;
}) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });
  return (
    <BadgeAssigneeEditAttributeInput
      attribute={attribute}
      error={fieldState.error?.message}
      inlineMetadata={inlineMetadata}
      isBottomSheet={isBottomSheet}
      onChange={field.onChange}
      value={field.value}
    />
  );
}

export function BadgeAssigneeEditAttributeInput({
  attribute,
  error,
  inlineMetadata = false,
  isBottomSheet = false,
  onChange,
  testIDPrefix = 'BadgeAssigneeAttributeInput',
  value,
}: {
  attribute: {
    attribute_type: string;
    id: string;
    is_required: boolean;
    options?: readonly string[] | null;
    title: string;
  };
  error?: string;
  inlineMetadata?: boolean;
  isBottomSheet?: boolean;
  onChange: (value: string) => void;
  testIDPrefix?: string;
  value?: string | null;
}) {
  const typeLabel =
    attribute.attribute_type === 'numeric'
      ? attributeTypeLabel.numeric
      : attributeTypeLabel.text;

  return (
    <Box direction="vertical" space="xs" w="full">
      <Box
        direction="horizontal"
        space="sm"
        align={inlineMetadata ? 'center' : 'flex-end'}
        justifyContent={inlineMetadata ? 'flex-start' : 'space-between'}
        wrap
      >
        <Box
          direction="horizontal"
          space={inlineMetadata ? 'md' : 'sm'}
          align={inlineMetadata ? 'center' : 'flex-end'}
          flexShrink={1}
          wrap
        >
          <UIText bold size="xl" flexShrink={1}>
            {attribute.title}
          </UIText>

          {inlineMetadata ? (
            <BadgeAssigneeAttributeMetadata
              error={error}
              isRequired={attribute.is_required}
              typeLabel={attribute.options?.length ? 'Choice' : typeLabel}
            />
          ) : null}
        </Box>

        {!inlineMetadata ? (
          <BadgeAssigneeAttributeMetadata
            error={error}
            isRequired={attribute.is_required}
            typeLabel={attribute.options?.length ? 'Choice' : typeLabel}
          />
        ) : null}
      </Box>

      <Box direction="horizontal" space="xs" align="flex-start" wrap>
        {attribute.options?.length ? (
          <Box flex>
            <Select
              onValueChange={onChange}
              value={value}
              placeholder="Choose..."
              testID={`${testIDPrefix}-${attribute.title}`}
              options={[
                {
                  label: 'No Selection',
                  value: '',
                },
                ...attribute.options.map((option) => ({
                  label: option,
                  value: option,
                })),
              ]}
            />
          </Box>
        ) : (
          <Box flex>
            <TextInput
              accessibilityLabel={attribute.title}
              isBottomSheet={isBottomSheet}
              onChangeText={onChange}
              placeholder={'Enter Response...'}
              value={value ?? ''}
              testID={`${testIDPrefix}-${attribute.title}`}
              inputMode={
                attribute.attribute_type === 'numeric' ? 'decimal' : 'text'
              }
              enterKeyHint="done"
              isInvalid={Boolean(error)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function BadgeAssigneeAttributeMetadata({
  error,
  isRequired,
  typeLabel,
}: {
  error?: string;
  isRequired: boolean;
  typeLabel: string;
}) {
  return (
    <Box direction="horizontal" space="xs" align="center">
      {isRequired || error ? (
        <>
          <UIText
            numberOfLines={1}
            size="xs"
            variant={error ? 'error' : 'typographyMuted'}
          >
            {error || 'Required'}
          </UIText>
          <UIText size="xs" variant="typographyMuted">
            |
          </UIText>
        </>
      ) : null}

      <UIText numberOfLines={1} size="xs" variant="typographyMuted">
        {typeLabel}
      </UIText>
    </Box>
  );
}

export default BadgeAssigneeEditAttribute;
